import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Optional trackers either host app may ask about. Not every app uses every
 * one — admin has no ChurnZero, and Mixpanel is currently a no-op stub in both
 * — but a single shared union keeps the record format identical across apps,
 * and an app simply never grants a name it never asks about.
 *
 * Adding a name here is the whole change on this end — the grant sets below
 * derive from it, and an absent name reads as denied, so existing records keep
 * their meaning.
 */
export type TrackerKey = 'mixpanel' | 'site24x7' | 'productfruits' | 'churnzero';
export type TrackerGrants = Readonly<Record<TrackerKey, boolean>>;

/** Order matters: save() joins the granted names in this order. */
export const TRACKER_KEYS: readonly TrackerKey[] = ['mixpanel', 'site24x7', 'productfruits', 'churnzero'];

/** Builds a full grant set from one list, so adding a tracker means editing TRACKER_KEYS only. */
function grantsFrom(allow: (key: TrackerKey) => boolean): TrackerGrants {
    const grants = {} as Record<TrackerKey, boolean>;
    TRACKER_KEYS.forEach((key) => (grants[key] = allow(key)));
    return grants;
}

export const NO_GRANTS: TrackerGrants = grantsFrom(() => false);
export const ALL_GRANTS: TrackerGrants = grantsFrom(() => true);

/**
 * Trackers that need a fresh document before a withdrawal takes effect,
 * because they arrive as a `<script>` that cannot be recalled once loaded, or
 * (ProductFruits) inject their own chain of scripts the moment init() runs.
 * Mixpanel is not one of these: it is an npm import whose own opt-out stops
 * collection immediately, with no reload needed.
 */
export const REQUIRES_RELOAD_TO_WITHDRAW: readonly TrackerKey[] = ['site24x7', 'productfruits', 'churnzero'];

export const CONSENT_COOKIE_NAME = 'oxm_cookie_consent';
const PROBE_COOKIE_NAME = '__oxm_cookie_probe';
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const NO_GRANTS_TOKEN = 'none';
const GRANT_SEPARATOR = '-';
// v<version>.<epochSeconds>.<grantList> — the version and a positive timestamp
// are part of the pattern, so a stale or malformed record simply fails to
// match and the app re-prompts rather than misreading it.
const RECORD_PATTERN = new RegExp(`^v${CONSENT_VERSION}\\.[1-9]\\d*\\.([a-z0-9-]{1,64})$`);

/**
 * Owns the cookie-consent record and is the single source of truth for it,
 * shared by both host apps.
 *
 * The record lives in a first-party cookie rather than localStorage on
 * purpose: consent *to cookies* must not outlive the cookies it consents to,
 * and each app's own session-clearing on sign-out already wipes
 * localStorage, which would otherwise re-prompt every user on every login.
 *
 * Grants are stored as a name list rather than positional flags, so adding a
 * tracker later cannot change what an existing record means: an absent name
 * is simply denied, and no migration is needed.
 *
 * A rejection is stored the same way an acceptance is — as a real, recorded
 * decision — so the answer is not re-asked on every page load. This is the
 * only place in either app that touches document.cookie for consent.
 */
@Injectable({
    providedIn: 'root'
})
export class CookieConsentService {
    // A plain Subject, not a BehaviorSubject: consent$ reports *changes* only.
    // Callers that need the current state read getGrants()/isAllowed(), which
    // go straight to the cookie — the authoritative store.
    private readonly grantsSubject = new Subject<TrackerGrants>();
    public readonly consent$: Observable<TrackerGrants> = this.grantsSubject.asObservable();

    // The modal is a singleton mounted once in each app's root component, so
    // reopening it from elsewhere (the account menu, the cookie-policy page,
    // or a post-SSO dashboard gate) needs a signal rather than a direct reference.
    /** Set by save() when this page load recorded a grant; never persisted. */
    private consentGrantedThisPageLoad = false;

    private readonly reopenSubject = new Subject<'gate' | 'settings'>();
    public readonly reopenRequest$: Observable<'gate' | 'settings'> =
        this.reopenSubject.asObservable();

    /** Asks the mounted consent modal to reopen in its dismissable, "settings" mode. */
    public requestReopen(): void {
        this.reopenSubject.next('settings');
    }

    /**
     * Opens the non-dismissable sign-in/landing gate when no decision exists yet.
     * Used for DPD SSO users who land on the dashboard without visiting /login.
     */
    public requestGate(): void {
        if (this.hasDecision()) return;
        this.reopenSubject.next('gate');
    }

    /** Denied for everything when no valid record exists — fail closed. */
    public getGrants(): TrackerGrants {
        const tokens = this.readGrantTokens();
        return grantsFrom((key) => tokens.includes(key));
    }

    /** True once the user has answered, whichever way. This is the sign-in gate. */
    public hasDecision(): boolean {
        return this.readGrantTokens().length > 0;
    }

    public isAllowed(tracker: TrackerKey): boolean {
        return this.readGrantTokens().includes(tracker);
    }

    /** Returns whether the record actually stuck; a blocked write returns false. */
    public save(grants: TrackerGrants): boolean {
        const granted = TRACKER_KEYS.filter((key) => grants[key]);
        const list = granted.length > 0 ? granted.join(GRANT_SEPARATOR) : NO_GRANTS_TOKEN;
        const seconds = Math.floor(Date.now() / 1000);
        this.writeCookie(CONSENT_COOKIE_NAME, `v${CONSENT_VERSION}.${seconds}.${list}`, CONSENT_MAX_AGE_SECONDS);
        // Re-read once rather than assume: a blocked write must report what is
        // actually stored. An empty token list means nothing stuck — which is
        // not the same as a stored `none`, a real decision to grant nothing.
        const tokens = this.readGrantTokens();
        if (TRACKER_KEYS.some((key) => tokens.includes(key))) {
            this.consentGrantedThisPageLoad = true;
        }
        this.grantsSubject.next(grantsFrom((key) => tokens.includes(key)));
        return tokens.length > 0;
    }

    /**
     * True when the user has recorded a decision that allows at least one
     * tracker. The pre-merge service persisted only acceptances, so its
     * hasAcceptedCookies() meant "a record exists"; under the per-tracker
     * model a rejection is recorded too, hence the explicit grant check.
     */
    public hasAcceptedCookies(): boolean {
        return TRACKER_KEYS.some((key) => this.isAllowed(key));
    }

    /** True when the user answered and granted nothing. */
    public hasDeniedCookies(): boolean {
        return this.hasDecision() && !this.hasAcceptedCookies();
    }

    /**
     * True when the grant was made during this page load, so sign-in can
     * report the fresh consent to the backend once. Session-only: a reload
     * clears it, exactly as the pre-merge service behaved.
     */
    public wasConsentJustGranted(): boolean {
        return this.consentGrantedThisPageLoad;
    }

    public acceptAll(): boolean {
        return this.save(ALL_GRANTS);
    }

    public rejectAll(): boolean {
        return this.save(NO_GRANTS);
    }

    /**
     * navigator.cookieEnabled reports true in contexts where writes are
     * silently dropped, so it is a fast pre-check only — the probe is the
     * verdict. Costs two cookie writes, so keep it off hot paths.
     */
    public areCookiesUsable(): boolean {
        if (!navigator.cookieEnabled) return false;
        this.writeCookie(PROBE_COOKIE_NAME, '1', 60);
        const usable = this.readCookie(PROBE_COOKIE_NAME) === '1';
        this.deleteCookie(PROBE_COOKIE_NAME);
        return usable;
    }

    /** Tokens of a valid record, or an empty list when there is no valid record. */
    private readGrantTokens(): readonly string[] {
        const raw = this.readCookie(CONSENT_COOKIE_NAME);
        const list = raw && RECORD_PATTERN.exec(raw)?.[1];
        return list ? list.split(GRANT_SEPARATOR) : [];
    }

    private readCookie(name: string): string | null {
        const prefix = `${name}=`;
        const entry = document.cookie.split('; ').find((part) => part.startsWith(prefix));
        return entry ? decodeURIComponent(entry.substring(prefix.length)) : null;
    }

    private writeCookie(name: string, value: string, maxAgeSeconds: number): void {
        const attributes = ['Path=/', 'SameSite=Lax', `Max-Age=${maxAgeSeconds}`];
        // Host-only: no Domain attribute, so the record never travels to the API host.
        // Gate Secure on the scheme, not on window.isSecureContext: http://localhost
        // is a secure context, but WebKit refuses to store a Secure cookie over
        // plain http, so on Safari the write would silently no-op during dev.
        if (location.protocol === 'https:') attributes.push('Secure');
        document.cookie = `${name}=${encodeURIComponent(value)}; ${attributes.join('; ')}`;
    }

    private deleteCookie(name: string): void {
        document.cookie = `${name}=; Path=/; Max-Age=0`;
    }
}
